/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped payloads, read field by field by the settings cards */
import { SdkContext } from '../internal'
import { versionedSettingsWrite } from '@/lib/realtime/settingsVersion'

/* The community's brand images move the settings version (`settingsUpdatedAt`). */
const settingsField = (v: string | null | undefined): Record<string, string> => (v ? { settingsUpdatedAt: v } : {})

export interface UploadedAvatar {
  url: string
  [key: string]: any
}

export class UploadResource {
  constructor(private ctx: SdkContext) {}

  private formData(file: File, field: string, extra?: Record<string, string>): FormData {
    const fd = new FormData()
    fd.append(field, file)
    if (extra) {
      for (const [k, v] of Object.entries(extra)) fd.append(k, v)
    }
    return fd
  }

  async uploadAvatar(file: File): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/avatar', hasBody: true },
      { body: this.formData(file, 'avatar') }
    )
  }

  /** A member's photo in this community: the session's own without `memberId`; the team passes another member's. */
  async uploadMemberAvatar(file: File, memberId?: string): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/avatar/member', hasBody: true },
      { body: this.formData(file, 'avatar', memberId ? { memberId } : undefined) }
    )
  }

  async deleteMemberAvatar(memberId?: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/upload/avatar/member', hasBody: true },
      { body: memberId ? { memberId } : {} }
    )
  }

  /** F-177: the session's own cover on the member page (field `cover`, 3:1). */
  async uploadMemberCover(file: File): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/cover/member', hasBody: true },
      { body: this.formData(file, 'cover') }
    )
  }

  async deleteMemberCover(): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/upload/cover/member', hasBody: true }, { body: {} })
  }

  async uploadCommunityLogo(file: File, communityId: string): Promise<UploadedAvatar> {
    return versionedSettingsWrite((v) => this.ctx.call<UploadedAvatar>(
      { httpMethod: 'POST', path: '/upload/community/logo', hasBody: true },
      { body: this.formData(file, 'logo', { communityId, ...settingsField(v) }) }
    ))
  }

  /**
   * F-191: the logo for dark backgrounds. Same endpoint as the logo, with
   * `variant: dark`, so the file is stored as one of the community's logos
   * (the rule `PATCH /communities/appearance` checks) without replacing the
   * regular one. The caller saves the URL with `communities.updateAppearance`.
   */
  async uploadCommunityDarkLogo(file: File, communityId: string): Promise<UploadedAvatar> {
    return versionedSettingsWrite((v) => this.ctx.call<UploadedAvatar>(
      { httpMethod: 'POST', path: '/upload/community/logo', hasBody: true },
      { body: this.formData(file, 'logo', { communityId, variant: 'dark', ...settingsField(v) }) }
    ))
  }

  async uploadCommunityFavicon(file: File, communityId: string): Promise<UploadedAvatar> {
    return versionedSettingsWrite((v) => this.ctx.call<UploadedAvatar>(
      { httpMethod: 'POST', path: '/upload/community/favicon', hasBody: true },
      { body: this.formData(file, 'favicon', { communityId, ...settingsField(v) }) }
    ))
  }

  /**
   * A community banner (`community-banners/`), with nothing else changed: the
   * caller decides where the URL goes (F-119: the directory cover).
   */
  async uploadCommunityBanner(file: File, communityId: string): Promise<UploadedAvatar> {
    return versionedSettingsWrite((v) => this.ctx.call<UploadedAvatar>(
      { httpMethod: 'POST', path: '/upload/community/banner', hasBody: true },
      { body: this.formData(file, 'banner', { communityId, ...settingsField(v) }) }
    ))
  }

  async uploadCommunityOgImage(file: File, communityId: string): Promise<UploadedAvatar> {
    return versionedSettingsWrite((v) => this.ctx.call<UploadedAvatar>(
      { httpMethod: 'POST', path: '/upload/community/ogimage', hasBody: true },
      { body: this.formData(file, 'ogimage', { communityId, ...settingsField(v) }) }
    ))
  }

  async uploadBusinessDocument(file: File, communityId: string, documentType: 'identity' | 'address-proof'): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/business/document', hasBody: true },
      { body: this.formData(file, 'document', { communityId, documentType }) }
    )
  }

  async uploadPartnerDocument(file: File, partnerId: string, documentType: 'identity' | 'address-proof'): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/partner/document', hasBody: true },
      { body: this.formData(file, 'document', { partnerId, documentType }) }
    )
  }

  async uploadEventBanner(file: File, eventId: string): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/upload/event/{eventId}/banner`, hasBody: true },
      { eventId, body: this.formData(file, 'banner') }
    )
  }

  async uploadEventFeatured(file: File, eventId: string): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/upload/event/{eventId}/featured`, hasBody: true },
      { eventId, body: this.formData(file, 'featured') }
    )
  }

  async uploadCallForPapersBanner(file: File, callId: string): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/upload/call4papers/{callId}/banner`, hasBody: true },
      { callId, body: this.formData(file, 'banner') }
    )
  }

  async uploadCallForPapersFeatured(file: File, callId: string): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/upload/call4papers/{callId}/featured`, hasBody: true },
      { callId, body: this.formData(file, 'featured') }
    )
  }

  async uploadImage(file: File): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upload/content/image', hasBody: true },
      { body: this.formData(file, 'image') }
    )
  }

  async uploadGalleryImages(
    spaceId: string,
    files: File[],
    metadata?: {
      titles?: string[]
      descriptions?: string[]
      tags?: string[][]
    }
  ) {
    if (files.length > 50) {
      throw new Error('Maximum 50 images allowed per upload')
    }

    const fd = new FormData()
    files.forEach(file => {
      if (file.size > 100 * 1024 * 1024) {
        throw new Error(`File ${file.name} exceeds 100MB limit`)
      }
      fd.append('images', file)
    })
    if (metadata?.titles) fd.append('titles', JSON.stringify(metadata.titles))
    if (metadata?.descriptions) fd.append('descriptions', JSON.stringify(metadata.descriptions))
    if (metadata?.tags) fd.append('tags', JSON.stringify(metadata.tags))

    return this.ctx.call<UploadedAvatar>(
      { httpMethod: 'POST', path: '/upload/gallery/images', hasBody: true },
      { body: fd, query: { spaceId } }
    )
  }

  async uploadProductBanner(file: File, productId: string): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/upload/product/{productId}/banner`, hasBody: true },
      { productId, body: this.formData(file, 'banner') }
    )
  }

  async uploadProductFeatured(file: File, productId: string): Promise<UploadedAvatar> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/upload/product/{productId}/featured`, hasBody: true },
      { productId, body: this.formData(file, 'featured') }
    )
  }
}
